import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { seasonAPI } from '../lib/api';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from './ui/dropdown-menu';
import { Trophy, LogOut, Shield, Plus, Home, Mail, LayoutDashboard, Lock } from 'lucide-react';

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [seasonLocked, setSeasonLocked] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const check = async () => {
      try { const res = await seasonAPI.status(); setSeasonLocked(res.data.season_locked); } catch (e) { console.error(e); }
    };
    check();
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 transition-all duration-300">
        <div className={`max-w-[1240px] mx-auto px-4 sm:px-6 flex items-center justify-between transition-all duration-300 ${scrolled ? 'h-[56px] sm:h-[72px]' : 'h-[64px] sm:h-[120px]'}`}>
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg text-foreground hover:opacity-80 transition-opacity">
            <img src="/poolsfc-logo.png" alt="Pools FC" className={`rounded-lg transition-all duration-300 ${scrolled ? 'h-[36px] w-[36px] sm:h-[56px] sm:w-[56px]' : 'h-[48px] w-[48px] sm:h-[104px] sm:w-[104px]'}`} />
            <span className="hidden sm:inline">Pools FC</span>
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2">
            <Button variant={location.pathname === '/pools' ? 'secondary' : 'ghost'} size="sm" asChild>
              <Link to="/pools"><Home className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Pools</span></Link>
            </Button>
            <Button variant={location.pathname === '/dashboard' ? 'secondary' : 'ghost'} size="sm" asChild>
              <Link to="/dashboard"><LayoutDashboard className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Dashboard</span></Link>
            </Button>
            {!seasonLocked && (user?.role === 'admin' || user?.role === 'organizer') && (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/create-pool"><Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Create</span></Link>
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button variant={location.pathname === '/admin' ? 'secondary' : 'ghost'} size="sm" asChild>
                <Link to="/admin"><Shield className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Admin</span></Link>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary-foreground">{user?.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <span className="hidden sm:inline text-sm max-w-[100px] truncate">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize mt-0.5">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/contact')}>
                  <Mail className="h-4 w-4 mr-2" />Contact Us
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }}>
                  <LogOut className="h-4 w-4 mr-2" />Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </header>
      <main className="max-w-[1240px] mx-auto px-4 sm:px-6 py-6">
        {seasonLocked && (
          <div className="mb-4 rounded-xl bg-yellow-50 border border-yellow-200 px-4 py-3 flex items-center gap-3">
            <Lock className="h-4 w-4 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800">The season is currently locked. Pools are not accepting new entries, members, or changes.</p>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
